<template>
  <fieldset>
    <legend class="tw:text-sm tw:font-medium tw:mb-2">
      {{ $t('Where this field appears') }}
    </legend>

    <div class="tw:flex tw:flex-col tw:gap-3">
      <div
        v-for="place in contexts"
        :key="place.ctx"
        class="tw:flex tw:flex-col tw:gap-1 tw:sm:flex-row tw:sm:items-center tw:sm:gap-3"
      >
        <span class="tw:flex tw:items-center tw:gap-2 tw:text-sm tw:sm:w-32 tw:sm:shrink-0">
          <Icon :name="place.icon" class="tw:text-muted-foreground" />
          {{ $t(place.label) }}
        </span>

        <SegmentedControl
          class="tw:w-full tw:sm:flex-1"
          fullwidth
          :options="stateOptions(place.ctx)"
          :model-value="toFieldState(modelValue[place.ctx])"
          @update:model-value="onChange(place.ctx, $event as FieldState)"
        />
      </div>
    </div>
  </fieldset>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { Icon } from '@/components/ui/icon'
import SegmentedControl, { type SegmentedOption } from '@/components/ui/SegmentedControl.vue'
import contexts from './contexts'
import { stateOptions as options } from './states'
import {
  fromFieldState,
  toFieldState,
  type ContextKey,
  type FieldConfig,
  type FieldState,
  type VisibilityConfig,
} from '@/types/FieldConfig'

const props = defineProps<{
  modelValue: Pick<FieldConfig, ContextKey>;
  /** Choices the field itself forbids, so they are shown but cannot be picked. */
  readOnly?: Partial<Record<ContextKey, VisibilityConfig>>;
}>()

const emit = defineEmits<{
  (event: 'update:place', ctx: ContextKey, visibility: VisibilityConfig): void;
}>()

const { t } = useI18n()

const stateOptions = (ctx: ContextKey): SegmentedOption[] =>
  options(props.readOnly?.[ctx]).map(option => ({ ...option, label: t(option.label) }))

const onChange = (ctx: ContextKey, state: FieldState): void =>
  emit('update:place', ctx, fromFieldState(state))
</script>
